import React from "react";
import {
  ClipboardList,
  AlertCircle,
  Phone,
  Mail,
  MapPin,
  Edit3,
  Trash2,
  Wallet,
  DollarSign,
  CalendarDays,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { can } from "@/contexts/AuthContext";
import { money } from "@/lib/dateHelpers";
import { StatusBadge } from "@/components/dashboard/ClientList";
import IntakeDetailsPanel from "@/components/dashboard/IntakeDetailsPanel";
import StageTimeline from "@/components/dashboard/StageTimeline";
import SessionNotes, { SessionHistory } from "@/components/dashboard/SessionNotes";
import PaymentHistory from "@/components/PaymentHistory";
import ReminderButton from "@/components/dashboard/ReminderButton";

export default function ClientProfile({
  client,
  currentUser,
  paymentRefreshKey,
  onEdit,
  onDelete,
  onRecordPayment,
  onUpdateField,
  onAddSession,
}) {
  return (
    <div className="min-w-0 space-y-6" data-testid="client-detail">
      {/* Header card */}
      <Card className="rounded-2xl shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-3xl font-bold">{client.dogName}</h2>
                <StatusBadge status={client.status} />
              </div>
              <p className="mt-1 text-slate-600">
                {client.breed} • {client.age}
              </p>
              <p className="mt-2 font-medium">Owner: {client.ownerName}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {can(currentUser, "recordPayment") && (
                <Button
                  data-testid="record-payment-btn"
                  onClick={onRecordPayment}
                  className="rounded-2xl"
                >
                  <Wallet size={16} className="mr-2" />
                  Record Payment
                </Button>
              )}
              {can(currentUser, "addSession") && <ReminderButton client={client} />}
              {can(currentUser, "editClient") && (
                <Button
                  data-testid="edit-client-btn"
                  variant="outline"
                  onClick={onEdit}
                  className="rounded-2xl"
                >
                  <Edit3 size={16} className="mr-2" />
                  Edit
                </Button>
              )}
              {can(currentUser, "deleteClient") && (
                <Button
                  data-testid="delete-client-btn"
                  variant="outline"
                  onClick={onDelete}
                  className="rounded-2xl"
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete
                </Button>
              )}
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <ContactCell icon={<Phone size={18} />} label="Phone" value={client.phone} />
            <ContactCell icon={<Mail size={18} />} label="Email" value={client.email} />
            <ContactCell icon={<MapPin size={18} />} label="Location" value={client.address} />
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <BorderedPanel icon={<ClipboardList size={18} />} title="Training Goals">
              {client.goals || "No goals entered yet."}
            </BorderedPanel>
            <BorderedPanel icon={<AlertCircle size={18} />} title="Behavior Notes">
              {client.behaviorNotes || client.mainBehaviorIssue || "No behavior notes entered yet."}
            </BorderedPanel>
          </div>

          <IntakeDetailsPanel client={client} />
          <StageTimeline history={client.leadStageHistory} />
        </CardContent>
      </Card>

      {/* Status + Session row */}
      <div className="grid gap-6 xl:grid-cols-2">
        <ClientStatusCard client={client} currentUser={currentUser} onUpdateField={onUpdateField} />
        {can(currentUser, "addSession") && (
          <SessionNotes client={client} onAddSession={onAddSession} />
        )}
      </div>

      <SessionHistory client={client} />

      <PaymentHistory clientId={client.id} refreshKey={paymentRefreshKey} />
    </div>
  );
}

function ContactCell({ icon, label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-4">
      {icon}
      <p className="mt-2 text-sm text-slate-500">{label}</p>
      <p className="font-medium">{value || "—"}</p>
    </div>
  );
}

function BorderedPanel({ icon, title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 font-semibold">
        {icon} {title}
      </div>
      <p className="mt-2 text-slate-700">{children}</p>
    </div>
  );
}

function ClientStatusCard({ client, currentUser, onUpdateField }) {
  const editable = can(currentUser, "editClient");
  return (
    <Card className="rounded-2xl shadow-sm">
      <CardContent className="space-y-4 p-6">
        <h3 className="flex items-center gap-2 text-xl font-bold">
          <CalendarDays size={20} /> Client Status
        </h3>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="space-y-1 text-sm font-medium">
            Status
            <select
              data-testid="detail-status-select"
              value={client.status}
              onChange={(e) => onUpdateField("status", e.target.value)}
              className="w-full rounded-xl border p-3 font-normal"
            >
              <option>Lead</option>
              <option>Active</option>
              <option>Needs Follow-up</option>
              <option>Completed</option>
            </select>
          </label>
          <label className="space-y-1 text-sm font-medium">
            Next Session
            <input
              data-testid="detail-next-session"
              type="date"
              value={client.nextSession || ""}
              onChange={(e) => onUpdateField("nextSession", e.target.value)}
              className="w-full rounded-xl border p-3 font-normal"
            />
          </label>
          <label className="space-y-1 text-sm font-medium">
            Open Balance
            <input
              data-testid="detail-balance"
              type="number"
              disabled={!editable}
              value={client.balance || 0}
              onChange={(e) => onUpdateField("balance", Number(e.target.value))}
              className="w-full rounded-xl border p-3 font-normal disabled:bg-slate-50 disabled:text-slate-500"
            />
          </label>
          <label className="space-y-1 text-sm font-medium">
            Total Paid
            <input
              data-testid="detail-total-paid"
              type="number"
              disabled={!editable}
              value={client.totalPaid || 0}
              onChange={(e) => onUpdateField("totalPaid", Number(e.target.value))}
              className="w-full rounded-xl border p-3 font-normal disabled:bg-slate-50 disabled:text-slate-500"
            />
          </label>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-2xl bg-slate-50 p-4">
            <DollarSign size={18} />
            <p className="text-sm text-slate-500">Paid</p>
            <p className="text-2xl font-bold">{money(client.totalPaid)}</p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4">
            <DollarSign size={18} />
            <p className="text-sm text-slate-500">Balance</p>
            <p className="text-2xl font-bold">{money(client.balance)}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
